import type { Query, QueryFilters, QueryKey } from '@tanstack/react-query';

/**
 * The React Query keys the calendar's appointment LISTS are cached under, and
 * the one filter that matches all of them.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * There are now two list feeds, on two endpoints (PROD-2382):
 * `useAppointments` → `POST /api/Appointment`, and `useDeviceAppointments` →
 * `POST /api/Appointment/ResourceList`. Two feeds means two keys, and a key
 * that nothing else in the app knows about is a silent failure: every mutation
 * on this screen patches and invalidates the cached lists by key, so a device
 * booking, a status advance or a reschedule would appear to succeed and the
 * Devices tab — and the Technician Worklist — would keep showing the old row
 * until a reload. Nothing throws, nothing logs.
 *
 * So the key literals live here exactly once: the two hooks build their keys
 * from these functions, and every cache-wide consumer matches with
 * `APPOINTMENT_LIST_QUERIES` instead of naming a root. Adding a third list feed
 * is then one line in this file rather than a hunt through nine call sites.
 */

/** Root segment of the PROVIDER list key — `useAppointments`. */
export const APPOINTMENTS_QUERY_ROOT = 'appointments';

/** Root segment of the DEVICE list key — `useDeviceAppointments` (PROD-2382). */
export const DEVICE_APPOINTMENTS_QUERY_ROOT = 'device-appointments';

/** The key `useAppointments` registers under. Unchanged from before PROD-2382. */
export function appointmentsQueryKey(params: unknown): QueryKey {
  return [APPOINTMENTS_QUERY_ROOT, params];
}

/** The key `useDeviceAppointments` registers under. */
export function deviceAppointmentsQueryKey(params: unknown): QueryKey {
  return [DEVICE_APPOINTMENTS_QUERY_ROOT, params];
}

/**
 * Is this cached query one of the appointment LISTS?
 *
 * Root-segment only, deliberately: the provider root is also the prefix of
 * other list caches keyed `['appointments', …]` (the patient chart's tab, for
 * one), and those were already in scope of the `{ queryKey: ['appointments'] }`
 * prefix match this replaces. Widening to devices must not narrow anything.
 */
export function isAppointmentListQuery(query: Pick<Query, 'queryKey'>): boolean {
  const root = query.queryKey[0];
  return (
    root === APPOINTMENTS_QUERY_ROOT || root === DEVICE_APPOINTMENTS_QUERY_ROOT
  );
}

/**
 * The filter to hand `invalidateQueries` / `setQueriesData` / `findAll` when a
 * mutation has changed an appointment that any list might be showing.
 *
 * Replaces `{ queryKey: ['appointments'] }` at every such call site. A
 * predicate rather than a key, because React Query matches keys by PREFIX and
 * the two roots share none — there is no single key that spans both.
 */
export const APPOINTMENT_LIST_QUERIES: QueryFilters = {
  predicate: isAppointmentListQuery,
};
